import type { GuidedNotePreset } from './useEncounterTabConfig';

// PRD.300 — the clinic-type → guided-preset mapping now lives in data-access
// (visit-note-preferences.ts) so the preference resolver and the UI share ONE
// mapping. Re-exported here for existing callers.
export { guidedPresetForClinicType } from '@helios/data-access';

// ─── Types ───

export interface GuidedNoteCard {
  /** Unique card id */
  id: string;
  /** Display title for the card header */
  title: string;
  /** Icon name (lucide-style, matches ACCORDION_ICONS) */
  icon: string;
  /**
   * Submodule keys rendered inside this card.
   * A card with multiple keys is a presentation-layer grouping only —
   * each submodule keeps its own data/business logic.
   */
  submoduleKeys: string[];
  /**
   * When true, this section gets richer visual treatment (subtle background tint).
   * When false/omitted, the section renders as a lightweight document-like block.
   */
  emphasized?: boolean;
  /**
   * When true, the renderer should use internal pill/tab navigation
   * instead of stacking submodules vertically. Used for hero sections
   * like "Charting & Images" that have their own internal workspace.
   */
  hero?: boolean;
  /**
   * PRD.314 — lay the card's ACTIVE submodules out as a responsive grid rather
   * than stacking them with dividers. Used by the History group, where two
   * enabled histories sit side by side on desktop, one takes the full width,
   * and narrow screens stack. Purely presentational: each submodule keeps its
   * own data and write path.
   */
  columns?: boolean;
}

export interface GuidedNotePresetConfig {
  label: string;
  description: string;
  cards: GuidedNoteCard[];
}

// ─── Preset definitions ───

const GENERAL_PRESET: GuidedNotePresetConfig = {
  label: 'General',
  description: 'Standard clinical visit flow',
  // Mirrors the Aesthetic preset (same cards + Visual Assessment hero) but its
  // hero id is `visual-assessment-basic`, which the renderer maps to the visual
  // workspace WITHOUT the Before/After view (that's an aesthetic-only feature).
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233 — Chief Complaint as a first-class card right after Summary.
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    // PRD.314 — History is a PRESENTATION GROUP, not a clinical field. Its
    // children stay independently toggleable; `resolveGuidedNoteCards` drops
    // the whole card when none of them is visible, and `columns` lays out
    // whichever remain (two side by side, one full width, stacked on narrow).
    //
    // EVERY preset's card carries EVERY history key, because the renderer only
    // draws keys a card claims — a preference toggled on outside its card would
    // silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON), not the capability: PRD §17 requires a clinic
    // to be able to run Medical + Dental + Derma together without stored
    // history identity being derived from `clinic_type`.
    {
      id: 'history',
      title: 'History',
      icon: 'clipboard',
      submoduleKeys: ['medical_history', 'dental_history', 'derma_history'],
      columns: true,
    },
    {
      // Distinct id from Aesthetic's 'visual-assessment' so the renderer can
      // hide Before/After for General (see renderHeroSection in EncounterDetailPage).
      id: 'visual-assessment-basic',
      title: 'Visual Assessment',
      icon: 'camera',
      submoduleKeys: ['charting', 'files'],
      emphasized: true,
      hero: true,
    },
    // PRD.214 — Symptoms structured chip entry
    { id: 'symptoms', title: 'Symptoms', icon: 'clipboard', submoduleKeys: ['symptoms'] },
    // PRD.316 — Clinical Findings. Present in EVERY preset because the renderer
    // only draws keys a card claims: a preference toggled on outside its card
    // would silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON) — Dental only — not the capability.
    {
      id: 'clinical-findings',
      title: 'Clinical Findings',
      icon: 'clipboard',
      submoduleKeys: ['clinical_findings'],
    },
    // PRD.214 — Diagnosis structured chip entry
    { id: 'diagnosis', title: 'Diagnosis', icon: 'stethoscope', submoduleKeys: ['diagnoses'] },
    // PRD.319 — dental diagnostic/imaging documentation. Present in every
    // preset because the renderer only draws keys a card claims; only the
    // DEFAULT differs (Dental).
    {
      id: 'investigations',
      title: 'Investigations',
      icon: 'clipboard',
      submoduleKeys: ['investigations'],
    },
    // PRD.318 — the DENTAL treatment workflow (Treatment Plan + Treatment Done)
    // under one "Treatment" heading. Present in every preset because the
    // renderer only draws keys a card claims; only the DEFAULT differs — Dental
    // enables it, and does NOT additionally enable the generic Treatment Plan.
    {
      id: 'dental-treatment',
      title: 'Treatment',
      icon: 'clipboard',
      submoduleKeys: ['dental_treatment_plan'],
      emphasized: true,
    },
    {
      id: 'treatment-plan',
      title: 'Treatment Plan',
      icon: 'clipboard',
      submoduleKeys: ['treatment_plan'],
      emphasized: true,
    },
    { id: 'forms-consent', title: 'Forms & Consent', icon: 'file-text', submoduleKeys: ['forms'] },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    // PRD.305 — Labs card. Sits with Prescriptions because both are things the
    // clinician orders once the diagnosis is formed.
    { id: 'labs', title: 'Labs', icon: 'clipboard', submoduleKeys: ['labs'] },
  ],
};

const DENTAL_PRESET: GuidedNotePresetConfig = {
  label: 'Dental',
  description: 'Dental-focused visit with charting and treatment plan',
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    // PRD.314 — History is a PRESENTATION GROUP, not a clinical field. Its
    // children stay independently toggleable; `resolveGuidedNoteCards` drops
    // the whole card when none of them is visible, and `columns` lays out
    // whichever remain (two side by side, one full width, stacked on narrow).
    //
    // EVERY preset's card carries EVERY history key, because the renderer only
    // draws keys a card claims — a preference toggled on outside its card would
    // silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON), not the capability: PRD §17 requires a clinic
    // to be able to run Medical + Dental + Derma together without stored
    // history identity being derived from `clinic_type`.
    {
      id: 'history',
      title: 'History',
      icon: 'clipboard',
      submoduleKeys: ['medical_history', 'dental_history', 'derma_history'],
      columns: true,
    },
    {
      id: 'charting-images',
      title: 'Charting & Images',
      icon: 'edit',
      submoduleKeys: ['charting', 'files'],
      emphasized: true,
      /** Hero section: renderer should use internal pills instead of stacked submodules */
      hero: true,
    },
    // PRD.214 — structured chip entry, positioned as in the Aesthetic preset:
    // straight after the hero and ahead of the narrative note, matching
    // GUIDED_NOTE_TAB_ORDER.dental below.
    //
    // PRD.316/317 — Clinical Findings then sits between the chart and Diagnosis,
    // following the documented progression: the chart feeds findings
    // suggestions, and findings inform (but never generate) the diagnosis.
    { id: 'symptoms', title: 'Symptoms', icon: 'clipboard', submoduleKeys: ['symptoms'] },
    // PRD.316 — Clinical Findings. Present in EVERY preset because the renderer
    // only draws keys a card claims: a preference toggled on outside its card
    // would silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON) — Dental only — not the capability.
    {
      id: 'clinical-findings',
      title: 'Clinical Findings',
      icon: 'clipboard',
      submoduleKeys: ['clinical_findings'],
    },
    { id: 'diagnosis', title: 'Diagnosis', icon: 'stethoscope', submoduleKeys: ['diagnoses'] },
    {
      id: 'notes-findings',
      title: 'Notes & Findings',
      icon: 'file-text',
      submoduleKeys: ['notes'],
    },
    // PRD.319 — dental diagnostic/imaging documentation. Present in every
    // preset because the renderer only draws keys a card claims; only the
    // DEFAULT differs (Dental).
    {
      id: 'investigations',
      title: 'Investigations',
      icon: 'clipboard',
      submoduleKeys: ['investigations'],
    },
    // PRD.318 — the DENTAL treatment workflow (Treatment Plan + Treatment Done)
    // under one "Treatment" heading. Present in every preset because the
    // renderer only draws keys a card claims; only the DEFAULT differs — Dental
    // enables it, and does NOT additionally enable the generic Treatment Plan.
    {
      id: 'dental-treatment',
      title: 'Treatment',
      icon: 'clipboard',
      submoduleKeys: ['dental_treatment_plan'],
      emphasized: true,
    },
    {
      id: 'treatment-plan',
      title: 'Treatment Plan',
      icon: 'clipboard',
      submoduleKeys: ['treatment_plan'],
      emphasized: true,
    },
    { id: 'forms-consent', title: 'Forms & Consent', icon: 'file-text', submoduleKeys: ['forms'] },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    // PRD.305 — Labs card. Sits with Prescriptions because both are things the
    // clinician orders once the diagnosis is formed.
    { id: 'labs', title: 'Labs', icon: 'clipboard', submoduleKeys: ['labs'] },
    // Invoices + Payments moved to header Billing chip + panel (PRD.163)
  ],
};

const AESTHETIC_PRESET: GuidedNotePresetConfig = {
  label: 'Aesthetic',
  description: 'Aesthetic/cosmetic visit with visual assessment',
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    // PRD.314 — History is a PRESENTATION GROUP, not a clinical field. Its
    // children stay independently toggleable; `resolveGuidedNoteCards` drops
    // the whole card when none of them is visible, and `columns` lays out
    // whichever remain (two side by side, one full width, stacked on narrow).
    //
    // EVERY preset's card carries EVERY history key, because the renderer only
    // draws keys a card claims — a preference toggled on outside its card would
    // silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON), not the capability: PRD §17 requires a clinic
    // to be able to run Medical + Dental + Derma together without stored
    // history identity being derived from `clinic_type`.
    {
      id: 'history',
      title: 'History',
      icon: 'clipboard',
      submoduleKeys: ['medical_history', 'dental_history', 'derma_history'],
      columns: true,
    },
    {
      id: 'visual-assessment',
      title: 'Visual Assessment',
      icon: 'camera',
      submoduleKeys: ['charting', 'files'],
      emphasized: true,
      hero: true,
    },
    // PRD.214 — Split into separate structured chip entries
    { id: 'symptoms', title: 'Symptoms', icon: 'clipboard', submoduleKeys: ['symptoms'] },
    // PRD.316 — Clinical Findings. Present in EVERY preset because the renderer
    // only draws keys a card claims: a preference toggled on outside its card
    // would silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON) — Dental only — not the capability.
    {
      id: 'clinical-findings',
      title: 'Clinical Findings',
      icon: 'clipboard',
      submoduleKeys: ['clinical_findings'],
    },
    { id: 'diagnosis', title: 'Diagnosis', icon: 'stethoscope', submoduleKeys: ['diagnoses'] },
    // PRD.319 — dental diagnostic/imaging documentation. Present in every
    // preset because the renderer only draws keys a card claims; only the
    // DEFAULT differs (Dental).
    {
      id: 'investigations',
      title: 'Investigations',
      icon: 'clipboard',
      submoduleKeys: ['investigations'],
    },
    // PRD.318 — the DENTAL treatment workflow (Treatment Plan + Treatment Done)
    // under one "Treatment" heading. Present in every preset because the
    // renderer only draws keys a card claims; only the DEFAULT differs — Dental
    // enables it, and does NOT additionally enable the generic Treatment Plan.
    {
      id: 'dental-treatment',
      title: 'Treatment',
      icon: 'clipboard',
      submoduleKeys: ['dental_treatment_plan'],
      emphasized: true,
    },
    {
      id: 'treatment-plan',
      title: 'Treatment Plan',
      icon: 'clipboard',
      submoduleKeys: ['treatment_plan'],
      emphasized: true,
    },
    { id: 'forms-consent', title: 'Forms & Consent', icon: 'file-text', submoduleKeys: ['forms'] },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    // PRD.305 — Labs card. Sits with Prescriptions because both are things the
    // clinician orders once the diagnosis is formed.
    { id: 'labs', title: 'Labs', icon: 'clipboard', submoduleKeys: ['labs'] },
    // Invoices + Payments moved to header Billing chip + panel (PRD.163)
  ],
};

const CUSTOM_PRESET: GuidedNotePresetConfig = {
  label: 'Custom',
  description: 'All submodules in individual cards',
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    // PRD.314 — History is a PRESENTATION GROUP, not a clinical field. Its
    // children stay independently toggleable; `resolveGuidedNoteCards` drops
    // the whole card when none of them is visible, and `columns` lays out
    // whichever remain (two side by side, one full width, stacked on narrow).
    //
    // EVERY preset's card carries EVERY history key, because the renderer only
    // draws keys a card claims — a preference toggled on outside its card would
    // silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON), not the capability: PRD §17 requires a clinic
    // to be able to run Medical + Dental + Derma together without stored
    // history identity being derived from `clinic_type`.
    {
      id: 'history',
      title: 'History',
      icon: 'clipboard',
      submoduleKeys: ['medical_history', 'dental_history', 'derma_history'],
      columns: true,
    },
    { id: 'vitals', title: 'Vitals', icon: 'heart', submoduleKeys: ['vitals'] },
    { id: 'charting', title: 'Charting', icon: 'edit', submoduleKeys: ['charting'] },
    { id: 'symptoms', title: 'Symptoms', icon: 'clipboard', submoduleKeys: ['symptoms'] },
    // PRD.316 — Clinical Findings. Present in EVERY preset because the renderer
    // only draws keys a card claims: a preference toggled on outside its card
    // would silently render nothing. What differs per discipline is the DEFAULT
    // (GUIDED_PRESET_DEFAULT_ON) — Dental only — not the capability.
    {
      id: 'clinical-findings',
      title: 'Clinical Findings',
      icon: 'clipboard',
      submoduleKeys: ['clinical_findings'],
    },
    { id: 'diagnoses', title: 'Diagnosis', icon: 'stethoscope', submoduleKeys: ['diagnoses'] },
    { id: 'notes', title: 'Notes', icon: 'file-text', submoduleKeys: ['notes'] },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    // PRD.319 — dental diagnostic/imaging documentation. Present in every
    // preset because the renderer only draws keys a card claims; only the
    // DEFAULT differs (Dental).
    {
      id: 'investigations',
      title: 'Investigations',
      icon: 'clipboard',
      submoduleKeys: ['investigations'],
    },
    // PRD.318 — see the note on the other presets' Treatment cards.
    {
      id: 'dental-treatment',
      title: 'Treatment',
      icon: 'clipboard',
      submoduleKeys: ['dental_treatment_plan'],
    },
    {
      id: 'treatment-plan',
      title: 'Treatment Plan',
      icon: 'clipboard',
      submoduleKeys: ['treatment_plan'],
    },
    { id: 'forms', title: 'Forms', icon: 'file-text', submoduleKeys: ['forms'] },
    { id: 'labs', title: 'Labs', icon: 'clipboard', submoduleKeys: ['labs'] }, // PRD.305
    { id: 'files', title: 'Files', icon: 'folder-open', submoduleKeys: ['files'] },
    { id: 'invoices', title: 'Invoices', icon: 'credit-card', submoduleKeys: ['invoices'] },
    { id: 'payments', title: 'Payments', icon: 'credit-card', submoduleKeys: ['payments'] },
  ],
};

// ─── Preset registry ───

export const GUIDED_NOTE_PRESETS: Record<GuidedNotePreset, GuidedNotePresetConfig> = {
  general: GENERAL_PRESET,
  dental: DENTAL_PRESET,
  aesthetic: AESTHETIC_PRESET,
  custom: CUSTOM_PRESET,
};

/**
 * Resolve a preset, filtering out cards whose submodule keys are all disabled/hidden.
 * This gracefully handles missing/disabled submodules.
 */
export function resolveGuidedNoteCards(
  preset: GuidedNotePreset,
  visibleKeys: Set<string>,
): GuidedNoteCard[] {
  const config = GUIDED_NOTE_PRESETS[preset];
  if (!config) return GUIDED_NOTE_PRESETS.general.cards;

  return config.cards.filter((card) => card.submoduleKeys.some((key) => visibleKeys.has(key)));
}

// ─── Flat sub-module ordering per guided-note preset ───
// Used by the settings drawer to reorder the sub-module toggle list
// so it matches the clinical workflow of the selected preset.

export const GUIDED_NOTE_TAB_ORDER: Record<GuidedNotePreset, string[]> = {
  // General now mirrors Aesthetic's card flow (minus Before/After), so its tab
  // order matches Aesthetic — charting/files hero first, treatment plan, etc.
  general: [
    'summary',
    'chief_complaint', // PRD.233 — full-width section right after Summary.
    'medical_history', // PRD.310 — narrative history, straight after the complaint.
    'dental_history', // PRD.314
    'derma_history', // PRD.314
    'charting',
    'files',
    'symptoms', // PRD.214
    'clinical_findings', // PRD.316 — findings inform the diagnosis below
    'diagnoses', // PRD.214
    'notes',
    'investigations', // PRD.319
    'dental_treatment_plan', // PRD.318
    'treatment_plan',
    'forms',
    'prescriptions',
    'labs', // PRD.305
    'invoices',
    'payments',
    'vitals',
  ],
  dental: [
    'summary',
    'chief_complaint', // PRD.233
    'medical_history', // PRD.310
    'dental_history', // PRD.314
    'derma_history', // PRD.314
    'vitals',
    'charting',
    'files',
    'symptoms', // PRD.214
    'clinical_findings', // PRD.316 — findings inform the diagnosis below
    'diagnoses', // PRD.214
    'notes',
    'investigations', // PRD.319
    'dental_treatment_plan', // PRD.318
    'treatment_plan',
    'forms',
    'prescriptions',
    'labs', // PRD.305
    'invoices',
    'payments',
  ],
  aesthetic: [
    'summary',
    'chief_complaint', // PRD.233
    'medical_history', // PRD.310
    'dental_history', // PRD.314
    'derma_history', // PRD.314
    'charting',
    'files',
    'symptoms', // PRD.214
    'clinical_findings', // PRD.316 — findings inform the diagnosis below
    'diagnoses', // PRD.214
    'notes',
    'investigations', // PRD.319
    'dental_treatment_plan', // PRD.318
    'treatment_plan',
    'forms',
    'prescriptions',
    'labs', // PRD.305
    'invoices',
    'payments',
    'vitals',
  ],
  custom: [
    'summary',
    'chief_complaint', // PRD.233
    'medical_history', // PRD.310
    'dental_history', // PRD.314
    'derma_history', // PRD.314
    'vitals',
    'charting',
    'symptoms', // PRD.214
    'clinical_findings', // PRD.316 — findings inform the diagnosis below
    'diagnoses', // PRD.214
    'notes',
    'prescriptions',
    'investigations', // PRD.319
    'dental_treatment_plan', // PRD.318
    'treatment_plan',
    'forms',
    'labs', // PRD.305
    'files',
    'invoices',
    'payments',
  ],
};

/**
 * Reorder a TabConfig[] to match the guided-note preset's intended workflow.
 * Tabs not in the preset order are appended at the end.
 */
export function reorderTabsForPreset(
  tabs: readonly { key: string; label: string; visible: boolean }[],
  preset: GuidedNotePreset,
): { key: string; label: string; visible: boolean }[] {
  const order = GUIDED_NOTE_TAB_ORDER[preset];
  if (!order) return [...tabs];

  const byKey = new Map(tabs.map((t) => [t.key, t]));
  const result: { key: string; label: string; visible: boolean }[] = [];

  for (const key of order) {
    const tab = byKey.get(key);
    if (tab) {
      result.push(tab);
      byKey.delete(key);
    }
  }
  // Append any remaining tabs not in the preset order
  for (const tab of byKey.values()) {
    result.push(tab);
  }
  return result;
}
